<template>
  <div class="lesson-content">
    <h1>Install AWS CLI</h1>
    <p>In this lesson, we'll install and verify the AWS Command Line Interface (CLI) using Homebrew. AWS CLI (Command Line Interface) is a unified tool to manage AWS services directly from your terminal.
      It allows you to control, automate, and script cloud resources efficiently.</p>

    <h2>Step 1: Install AWS CLI using Homebrew</h2>
    <p>Open your terminal and run:</p>
    <div class="code-block">
      <pre><code>brew install awscli</code></pre>
      <button class="copy-button" @click="copyInstallCommand" title="Copy to clipboard">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>

    <h2>Step 2: Verify Installation</h2>
    <p>After the installation completes, verify that AWS CLI is correctly installed by checking the version:</p>
    <div class="code-block">
      <pre><code>aws --version</code></pre>
      <button class="copy-button" @click="copyVersionCommand" title="Copy to clipboard">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>
    </div>
    <p>This command should output something similar to:</p>
    <pre><code class="output">aws-cli/2.15.8 Python/3.11.6 Darwin/23.3.0 source/arm64 prompt/off</code></pre>
    <p>If you see the version information as shown above, AWS CLI is installed successfully.</p>
  </div>
</template>

<script>
export default {
  name: 'Chapter1Lesson2',
  methods: {
    async copyToClipboard(text, button) {
      await navigator.clipboard.writeText(text);
      button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
      setTimeout(() => {
        button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>';
      }, 2000);
    },
    copyInstallCommand(event) {
      const command = 'brew install awscli';
      this.copyToClipboard(command, event.currentTarget);
    },
    copyVersionCommand(event) {
      const command = 'aws --version';
      this.copyToClipboard(command, event.currentTarget);
    }
  }
}
</script>

<style scoped>
.lesson-content {
  color: var(--text);
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

h1 {
  color: var(--primary);
  font-size: 2rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
}

h2 {
  color: var(--text);
  font-size: 1.5rem;
  margin: 2rem 0 1rem;
  font-weight: 600;
}

p {
  margin-bottom: 1rem;
}

ul, ol {
  margin: 1rem 0;
  padding-left: 1.5rem;
}

li {
  margin-bottom: 0.5rem;
}

pre {
  background: #f8fafc;
  border-radius: 6px;
  padding: 1rem;
  margin: 1rem 0;
  overflow-x: auto;
  border: 1px solid var(--border);
}

code {
  font-family: 'Fira Code', 'Consolas', monospace;
  font-size: 0.9rem;
}

p code {
  background: #f8fafc;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-size: 0.9rem;
}

code.output {
  color: #16a34a;  /* Green color for output text */
}

.code-block {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.code-block pre {
  flex-grow: 1;
  margin-right: 0.5rem;
}

.copy-button {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: var(--text);
  opacity: 0.7;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.copy-button:hover {
  opacity: 1;
}

.copy-button svg {
  width: 20px;
  height: 20px;
}
</style> 